<script setup lang="ts">

import { computed, defineProps } from 'vue'
import { usePlanetStore } from '@/stores/planetStore.ts'
import StandardCard from '@/components/Elements/Cards/StandardCard.vue'
import NamedNumbers from '@/components/Elements/Generic/NamedNumbers.vue'
import GenericIconWithColoredNumber from '@/components/Elements/Images/Icons/GenericIconWithColoredNumber.vue'
import SimpleProgressBar from '@/components/Elements/ProgressBar/SimpleProgressBar.vue'

/**
 * Props
 */
const props = defineProps({
  planetId: {
    type: Number,
    required: true
  }
});

/**
 * Stores / models
 */
const planetStore = usePlanetStore()


/**
 * Computed
 */
const planetData = computed(() => {
  return planetStore.findPlanetById(props.planetId)!
})

const correctedPopulationGrowthRequired = computed(() => {
  const population = planetData.value.population
  const isRingworld = Number(planetData.value.type) === 8
  
  // Cap non-ringworld planets to population 30
  const cappedPopulation = isRingworld ? population : Math.min(population, 30)
  
  // (population^2.0 ÷ 3) * 1.5 + 1
  return Math.floor(((Math.pow(cappedPopulation, 2.0) / 3) * 1.5)) + 1
})

const percentage = computed(() => {
  return (planetData.value.populationGrowthProcess / correctedPopulationGrowthRequired.value) * 100
})

const etaText = computed(() => {
  const amount = Math.ceil((correctedPopulationGrowthRequired.value - planetData.value.populationGrowthProcess) / planetData.value.populationGrowthPerTurn)
  if(amount <= 0) return '1 week'
  return amount + ' weeks'
})

const isAtPopulationCap = computed(() => {
  const isRingworld = Number(planetData.value.type) === 8
  return !isRingworld && planetData.value.population >= 30
})


</script>

<template>
<StandardCard v-if="isAtPopulationCap" title="Population Growth Limit Reached" top-padding="2">
    Your planet has reached the <span class="text-red-500">maximum population</span> growth limit of 30 billion inhabitants for regular planets.
    To continue growing your population beyond this limit, you need to <span class="text-slate-400">convert this solar system to a Ringworld</span>.
    Ringworlds offer unlimited population growth potential and access to advanced buildings.
</StandardCard>
<StandardCard v-else title="Population" top-padding="2" help="The population of your planet grows over time, all you need to do to increase your population is to keep producing a surplus of food. However, the larger the population, the more food is required to grow it.">

  <div class="flex justify-between pb-4">
    <NamedNumbers text="Population">
      <GenericIconWithColoredNumber :amount="planetData.population" type="person"/>
    </NamedNumbers>

    <NamedNumbers text="Food surplus">
      <GenericIconWithColoredNumber :amount="planetData.populationGrowthPerTurn" type="food"/>
    </NamedNumbers>
  </div>
  <SimpleProgressBar :percentage="percentage" :text="etaText" color="blue" :height="6"/>
  <div class="flex justify-between pt-4">
    <NamedNumbers text="Stored">
      <GenericIconWithColoredNumber :amount="planetData.populationGrowthProcess" type="food"/>
    </NamedNumbers>

    <NamedNumbers text="Required">
      <GenericIconWithColoredNumber :amount="correctedPopulationGrowthRequired" type="food"/>
    </NamedNumbers>
  </div>
</StandardCard>


</template>

<style scoped lang="scss">

</style>
